import { useContext, useEffect, useRef } from 'react';
import { ChatQueryStatus } from '@trinity/chat';
import type { LiveAvatarTTSResponse } from '@trinity/chat/types/heygen';
import {
  ActiveChatSessionContext,
  ChatSessionQueriesContext,
} from '@trinity/chat-components/components/Chat/context.js';
import { useTelemetryLogger } from '@trinity/observability-web/react';
import { useChatApiClient } from '../../containers/Chat/useApiClient.js';
import { useLiveAvatarContextOptional } from './LiveAvatar.context';

const IN_FLIGHT_STATUSES = new Set<ChatQueryStatus>([
  ChatQueryStatus.Loading,
  ChatQueryStatus.LoadingExtended,
  ChatQueryStatus.Streaming,
]);

const COMPLETE_STATUSES = new Set<ChatQueryStatus>([
  ChatQueryStatus.Complete,
  ChatQueryStatus.FollowupsAvailable,
]);

/**
 * PCM segment size in base64 characters, targeting ~8 seconds of audio per repeatAudio() call.
 *
 * The backend returns 24 kHz / 16-bit / mono PCM encoded as base64.
 * At 24,000 Hz, 16-bit (2 bytes/sample):
 *   bytes per second = 24,000 × 2 = 48,000
 *   8 seconds        = 384,000 PCM bytes
 *   base64 chars     = ceil(384,000 / 3) × 4 = 512,000
 *
 * Calling repeatAudio() with ~512,000-char segments instead of the full buffer
 * limits the HeyGen SDK's internal splitPcm24kStringToChunks loop to ~533
 * WebSocket frames per call (versus 12,000+ for a long response), preventing
 * the send-buffer overflow that causes random audio skipping.
 */
const PCM_SEGMENT_BASE64_CHARS = 512_000;

/** Approximate playback duration for one segment (ms). */
const SEGMENT_DURATION_MS = 8_000;

/**
 * How many ms before a segment finishes to fire the next repeatAudio() call.
 * Fires slightly early so the SDK can buffer the next segment before
 * the previous one ends, avoiding an audible gap between segments.
 */
const SEGMENT_OVERLAP_MS = 200;

/**
 * Split `audioData` (full base64-encoded PCM from the backend) into ~8-second
 * segments and call `session.repeatAudio()` for each one, paced over time so
 * the SDK never receives more than ~533 WebSocket frames at once.
 *
 * Segment 0 is sent immediately. Each subsequent segment is scheduled with
 * setTimeout so it arrives just before the previous segment finishes playing.
 * All timeout IDs are pushed into `pendingTimeoutsRef` so they can be cancelled
 * when a new response arrives or the component unmounts.
 */
function paceRepeatAudio(
  session: { repeatAudio: (audio: string) => void },
  audioData: string,
  pendingTimeoutsRef: React.MutableRefObject<ReturnType<typeof setTimeout>[]>,
): void {
  const segments: string[] = [];
  for (let i = 0; i < audioData.length; i += PCM_SEGMENT_BASE64_CHARS) {
    segments.push(audioData.slice(i, i + PCM_SEGMENT_BASE64_CHARS));
  }

  segments.forEach((segment, index) => {
    if (index === 0) {
      session.repeatAudio(segment);
      return;
    }
    const delayMs = index * SEGMENT_DURATION_MS - SEGMENT_OVERLAP_MS;
    const timeoutId = setTimeout(() => {
      try {
        session.repeatAudio(segment);
      } catch (err: unknown) {
        // Swallow — the session may have closed between scheduling and firing.
        console.error('[HeyGenResponseBridge] Failed to repeat audio segment:', err);
      }
    }, delayMs);
    pendingTimeoutsRef.current.push(timeoutId);
  });
}

/**
 * HeyGenResponseBridge
 *
 * Renderless component (returns null) that bridges AI query responses to the
 * HeyGen avatar in LITE mode.
 *
 * How it works:
 *   1. Watches `queries[currentQueryIdentifier].status` for the transition
 *      Loading|Streaming → Complete|FollowupsAvailable, which signals that
 *      the current query's AI response has fully arrived.
 *   2. Reads the completed query's response text from ChatSessionQueriesContext.
 *   3. Drives the avatar to speak the response via backend TTS.
 *
 * LITE mode TTS flow:
 *   - POST /v1/chat/live-avatar/tts { text: responseText, ttsConfig } → { audio: base64 }
 *   - The base64 PCM is split into ~8-second segments via paceRepeatAudio() and
 *     delivered to sessionRef.current.repeatAudio() one at a time, timed to actual
 *     playback duration. This prevents the SDK's splitPcm24kStringToChunks loop
 *     from flooding the WebSocket send buffer on long responses (which caused
 *     random audio skipping).
 *
 * Must be rendered as a descendant of LiveAvatarSessionManager.
 * Must also be rendered inside the chat provider tree (inside ChatContainer)
 * so that ActiveChatSessionContext and ChatSessionQueriesContext are in scope.
 */
export function HeyGenResponseBridge(): null {
  const ctx = useLiveAvatarContextOptional();
  const apiClient = useChatApiClient();
  const loggerCtx = useTelemetryLogger();

  const chatInstance = useContext(ActiveChatSessionContext);
  const queries = useContext(ChatSessionQueriesContext);

  const currentQueryId = chatInstance?.currentQueryIdentifier ?? null;
  const currentQueryStatus = (currentQueryId ? queries?.[currentQueryId]?.status : null) ?? null;

  // Keep a stable ref to the latest queries map so our effect closure never
  // goes stale when we read response.value at completion time.
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  // Track the previous query status to detect the in-flight → complete transition.
  const prevStatusRef = useRef<ChatQueryStatus | null>(null);

  // Guard against re-firing TTS for the same completed query on subsequent re-renders.
  const processedQueryIdsRef = useRef<Set<string>>(new Set());

  // Use the shared pendingAudioTimeoutsRef from context so AvatarControlBar
  // can cancel all queued segments when the user clicks Interrupt.
  // Fall back to a local ref if this bridge ever renders without a context.
  const localFallbackTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const pendingTimeoutsRef = ctx?.pendingAudioTimeoutsRef ?? localFallbackTimeoutsRef;

  // Cancel any queued audio segments when the component unmounts.
  useEffect(() => {
    const ref = pendingTimeoutsRef;
    return () => {
      ref.current.forEach(clearTimeout);
      ref.current = [];
    };
  }, []);

  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = currentQueryStatus;

    // Only act when transitioning from an in-flight status to a complete status.
    if (!prevStatus || !IN_FLIGHT_STATUSES.has(prevStatus)) {
      return;
    }
    if (!currentQueryStatus || !COMPLETE_STATUSES.has(currentQueryStatus)) {
      return;
    }

    // Require a known query ID to look up the response.
    if (!currentQueryId) {
      return;
    }

    // Don't fire TTS twice for the same completed query.
    if (processedQueryIdsRef.current.has(currentQueryId)) {
      return;
    }
    processedQueryIdsRef.current.add(currentQueryId);

    // No avatar session yet (context not provided or still loading).
    if (!ctx?.sessionRef.current) {
      return;
    }

    const completedQuery = queriesRef.current?.[currentQueryId];
    const responseText = completedQuery?.response?.value;

    if (!responseText) {
      return;
    }

    if (!ctx.ttsConfig) {
      // No TTS config on this segment — avatar is visible but silent.
      return;
    }

    // Cancel any audio segments still queued from the previous response
    // so a new response immediately takes over playback.
    pendingTimeoutsRef.current.forEach(clearTimeout);
    pendingTimeoutsRef.current = [];

    apiClient.authenticatedClient
      .post<LiveAvatarTTSResponse>('/chat/live-avatar/tts', {
        text: responseText,
        ttsConfig: ctx.ttsConfig,
        queryIdentifier: currentQueryId,
      })
      .then((res) => {
        const sessionAtResponse = ctx.sessionRef.current;

        if (!sessionAtResponse) {
          return;
        }

        try {
          // Split the full PCM buffer into ~8-second segments and deliver them
          // to the SDK one at a time, timed to match actual playback duration.
          // This prevents the SDK's internal splitPcm24kStringToChunks loop
          // from firing thousands of WebSocket frames at once, which caused
          // random audio skipping on long responses.
          paceRepeatAudio(sessionAtResponse, res.data.audio, pendingTimeoutsRef);
        } catch (repeatErr: unknown) {
          loggerCtx.captureException(repeatErr);
        }
      })
      .catch((err: unknown) => {
        loggerCtx.captureException(err);
      });
  }, [currentQueryId, currentQueryStatus, ctx, apiClient, loggerCtx]);

  return null;
}
